import { IndexChildContainer } from 'react-components/member-index/indexContainer';
import { TextField } from '@mui/material';
import { PaginatedTable, PaginatedTableProps } from '@act/storybook';
import { TeamsTableColumns } from './TeamsTableColumns';
import { useMemo, useState } from 'react';
import { TeamAdminViewDto } from '@act/shared/data-transfer-objects';
import { teamsSelectors } from 'redux/slices/teamsSlice';
import { internalPhoneNumbersSelectors } from 'redux/slices/internalPhoneNumberSlice';
import AddIcon from '@mui/icons-material/Add';
import { TeamCreateEdit } from './TeamCreateEdit';
import { AdminToolSkeleton } from '../AdminToolSkeleton';
import { phoneFormatter } from '@act/shared/utilities';

export const TeamsTable = () => {
  const allTeams = teamsSelectors.useSelectAll();
  const internalNumbers =
    internalPhoneNumbersSelectors.useSelectAllDefaultInternalNumbers();

  const [searchTerm, setSearchTerm] = useState('');
  const [createNewTeam, setCreateNewTeam] = useState(false);
  const [editTeamOpen, setEditTeamOpen] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState<TeamAdminViewDto | null>();
  const columns = useMemo(() => {
    return TeamsTableColumns(internalNumbers);
  }, [internalNumbers]);

  const teamsArray = Object.values(allTeams.entities || []);

  // search by name filtering
  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) {
      return teamsArray;
    }
    return teamsArray.filter(team => {
      const teamName = `${team.name}`.toLowerCase();
      return teamName.includes(searchTerm.toLowerCase());
    });
  }, [teamsArray, searchTerm]);

  const TEAMS_ADMIN_TABLE_PROPS: PaginatedTableProps<TeamAdminViewDto> = {
    data: filteredData,
    columnConfig: columns,
    inMemoryPagination: true,
    onRowClick: row => rowCallback(row),
  };

  const rowCallback = (row: TeamAdminViewDto) => {
    setSelectedTeam(row);
    setEditTeamOpen(true);
  };

  const setFormOpen = (value: boolean) => {
    if (!value) {
      setEditTeamOpen(false);
      setSelectedTeam(null);
    }
  };

  const phoneOptions = internalNumbers.map(number => {
    return {
      label: number?.displayName
        ? `${number?.displayName} : ${phoneFormatter.stringToPretty(
            number?.number
          )}`
        : `${number?.description} : ${phoneFormatter.stringToPretty(
            number?.number
          )}`,
      value: number.id,
    };
  });

  return (
    <AdminToolSkeleton
      title={'Teams Management'}
      toolDescription={'View, Add, or Modify Teams Here'}
      buttonContent={' Add New Team '}
      onClick={() => setCreateNewTeam(true)}
      startIcon={<AddIcon sx={{ ml: 1 }} />}
      variant="contained"
    >
      <TextField
        label="Search by Team Name"
        variant="outlined"
        value={searchTerm}
        onChange={e => setSearchTerm(e.target.value)}
        fullWidth
        margin="normal"
        sx={{ backgroundColor: '#ffffff' }}
      />
      <IndexChildContainer>
        <PaginatedTable {...TEAMS_ADMIN_TABLE_PROPS} />
      </IndexChildContainer>
      {(createNewTeam || editTeamOpen) && (
        <TeamCreateEdit
          setOpen={createNewTeam ? setCreateNewTeam : setFormOpen}
          phoneOptions={phoneOptions}
          isEdit={editTeamOpen}
          selectedTeam={selectedTeam}
          open={createNewTeam || editTeamOpen}
        />
      )}
    </AdminToolSkeleton>
  );
};
